import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useBillingUsage } from '../hooks/useBilling';
import NavBar from '../components/NavBar';
import { TimeRangeSelector, getTimeRangeParams } from '../components/TimeRangeSelector';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://kalibr-backend.fly.dev';

export default function Executions() {
  const navigate = useNavigate();
  const { getToken, isLoaded, isSignedIn, userId } = useAuth();

  const [executions, setExecutions] = useState([]);
  const [currentExecution, setCurrentExecution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState({ preset: '24h', startDate: null, endDate: null });

  // Get tenant from user ID
  const tenant = userId;

  // Fetch billing usage
  const { data: usage } = useBillingUsage({ enabled: !!tenant });

  useEffect(() => {
    if (isLoaded && isSignedIn && tenant) {
      fetchExecutions();
    }
  }, [isLoaded, isSignedIn, tenant, timeRange]);

  const fetchExecutions = async () => {
    try {
      setLoading(true);
      const token = await getToken();

      const timeParams = getTimeRangeParams(timeRange);
      const queryString = new URLSearchParams(timeParams).toString();
      const response = await fetch(`${API_BASE_URL}/api/executions?${queryString}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch');
      
      const data = await response.json();
      setExecutions(data.executions || []);
      
      // Fetch detail for first execution
      if (data.executions && data.executions.length > 0) {
        const firstId = data.executions[0].trace_id;
        const detailRes = await fetch(`${API_BASE_URL}/api/executions/${firstId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (detailRes.ok) {
          const detailData = await detailRes.json();
          setCurrentExecution(detailData.execution);
        }
      }
    } catch (err) {
      console.error('Error fetching executions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Auth loading state
  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-zinc-500">Loading...</div>
      </div>
    );
  }

  // Not signed in
  if (!isSignedIn) {
    navigate('/login');
    return null;
  }

  if (loading) return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-7xl mx-auto flex items-center justify-center h-64">
        <div className="text-zinc-500">Loading executions...</div>
      </div>
    </div>
  );

  const totalCost = executions.reduce((sum, e) => sum + (e.total_cost || 0), 0);

  // Group costs by provider
  const costByProvider = {};
  executions.forEach(exec => {
    const provider = exec.root_provider || 'Unknown';
    costByProvider[provider] = (costByProvider[provider] || 0) + (exec.total_cost || 0);
  });

  const providerCosts = Object.entries(costByProvider)
    .map(([provider, cost]) => ({
      provider,
      cost,
      percentage: totalCost > 0 ? (cost / totalCost) * 100 : 0
    }))
    .sort((a, b) => b.cost - a.cost);

  return (
    <div className="min-h-screen bg-black text-white">
      <NavBar />

      {/* Main Content */}
      <div className="p-8 pt-24">
        <div className="max-w-7xl mx-auto space-y-6">
          
          {/* Stats Bar */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold mb-1">Agent Analytics</h2>
              <p className="text-sm text-zinc-500">Real-time agent workflow analytics</p>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
              <div className="px-3 py-1.5 bg-zinc-900 rounded border border-zinc-800">
                <span className="text-zinc-500">Executions:</span>
                <span className="ml-2 text-white font-semibold">{executions.length}</span>
              </div>
              <div className="px-3 py-1.5 bg-zinc-900 rounded border border-zinc-800">
                <span className="text-zinc-500">Total cost:</span>
                <span className="ml-2 text-white font-semibold">${totalCost.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Top Row: Execution Chain + Cost Attribution */}
          <div className="grid grid-cols-3 gap-6">
            
            {/* Current Execution Chain (2/3 width) */}
            <div className="col-span-2 bg-zinc-950 border border-zinc-900 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold">
                  {currentExecution ? 'Current Execution' : 'No Recent Executions'}
                </h2>
                {currentExecution && (
                  <div className="text-xs px-2 py-1 bg-green-950 text-green-400 border border-green-900 rounded">
                    COMPLETE
                  </div>
                )}
              </div>
              
              {currentExecution ? (
                <div className="space-y-3">
                  {/* Parent Span */}
                  <div className="border-l-2 border-red-600 pl-4">
                    <div className="bg-zinc-900 border border-zinc-800 rounded p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                          <span className="font-semibold text-sm">{currentExecution.operation || 'Root Span'}</span>
                        </div>
                        <div className="text-xs text-zinc-500">Parent Span</div>
                      </div>
                      <div className="grid grid-cols-4 gap-3 text-xs">
                        <div>
                          <div className="text-zinc-500">Model</div>
                          <div className="text-white mt-0.5">{currentExecution.model_name || 'N/A'}</div>
                        </div>
                        <div>
                          <div className="text-zinc-500">Duration</div>
                          <div className="text-white mt-0.5">{((currentExecution.duration_ms || 0) / 1000).toFixed(1)}s</div>
                        </div>
                        <div>
                          <div className="text-zinc-500">Cost</div>
                          <div className="text-white mt-0.5">${(currentExecution.cost_usd || 0).toFixed(3)}</div>
                        </div>
                        <div>
                          <div className="text-zinc-500">Tokens</div>
                          <div className="text-white mt-0.5">
                            {(((currentExecution.input_tokens || 0) + (currentExecution.output_tokens || 0)) / 1000).toFixed(1)}K
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Child Spans */}
                  {(currentExecution.children || []).map((child, idx) => (
                    <div key={child.span_id || idx} className="ml-8 border-l-2 border-zinc-700 pl-4">
                      <div className="bg-zinc-900 border border-zinc-800 rounded p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-zinc-500 rounded-full"></div>
                            <span className="font-semibold text-sm">{child.operation || `Step ${idx + 1}`}</span>
                          </div>
                          <div className="text-xs text-zinc-500">Child Span</div>
                        </div>
                        <div className="grid grid-cols-4 gap-3 text-xs">
                          <div>
                            <div className="text-zinc-500">Model</div>
                            <div className="text-white mt-0.5">{child.model_name || 'N/A'}</div>
                          </div>
                          <div>
                            <div className="text-zinc-500">Duration</div>
                            <div className="text-white mt-0.5">{((child.duration_ms || 0) / 1000).toFixed(1)}s</div>
                          </div>
                          <div>
                            <div className="text-zinc-500">Cost</div>
                            <div className="text-white mt-0.5">${(child.cost_usd || 0).toFixed(3)}</div>
                          </div>
                          <div>
                            <div className="text-zinc-500">Tokens</div>
                            <div className="text-white mt-0.5">
                              {(((child.input_tokens || 0) + (child.output_tokens || 0)) / 1000).toFixed(1)}K
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-zinc-500 py-8">
                  No executions found. Run your first agent workflow to see data here.
                </div>
              )}
            </div>

            {/* Cost by Provider (1/3 width) */}
            <div className="bg-zinc-950 border border-zinc-900 rounded-lg p-6">
              <h2 className="text-lg font-bold mb-4">Cost by Provider</h2>
              {providerCosts.length > 0 ? (
                <div className="space-y-3">
                  {providerCosts.map((item) => (
                    <div key={item.provider}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-zinc-300">{item.provider}</span>
                        <span className="font-semibold">${item.cost.toFixed(2)}</span>
                      </div>
                      <div className="w-full bg-zinc-900 rounded-full h-2">
                        <div 
                          className="bg-red-600 h-2 rounded-full" 
                          style={{width: `${Math.max(item.percentage, 2)}%`}}
                        ></div>
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">{item.percentage.toFixed(0)}% of total</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-zinc-500 py-8 text-sm">
                  No cost data yet
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-zinc-800">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Total</span>
                  <span className="font-bold text-white">${totalCost.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Executions Table */}
          <div className="bg-zinc-950 border border-zinc-900 rounded-lg overflow-hidden">
            <div className="border-b border-zinc-900 px-6 py-4">
              <h2 className="text-lg font-semibold">All Executions</h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-zinc-800 text-left text-zinc-400 text-sm">
                  <tr>
                    <th className="px-6 py-3 font-medium">Trace ID</th>
                    <th className="px-6 py-3 font-medium">Provider</th>
                    <th className="px-6 py-3 font-medium">Steps</th>
                    <th className="px-6 py-3 font-medium">Cost</th>
                    <th className="px-6 py-3 font-medium">Duration</th>
                    <th className="px-6 py-3 font-medium">Status</th>
                    <th className="px-6 py-3 font-medium">Started</th>
                  </tr>
                </thead>
                <tbody>
                  {executions.length === 0 ? (
                    <>
                      {/* Ghost trace 1 */}
                      <tr className="border-b border-zinc-800 opacity-40 relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-zinc-700/10 to-transparent animate-shimmer"></div>
                        <td className="px-6 py-4 relative">
                          <div className="text-xs text-zinc-600 font-mono">a1b2c3d4e5f6g7h8...</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-zinc-600 relative">Anthropic</td>
                        <td className="px-6 py-4 text-sm text-zinc-600 relative">3</td>
                        <td className="px-6 py-4 text-sm font-mono text-zinc-600 relative">$0.0234</td>
                        <td className="px-6 py-4 text-sm text-zinc-600 relative">1.4s</td>
                        <td className="px-6 py-4 relative">
                          <span className="text-xs px-2 py-1 bg-green-950 text-green-700 border border-green-900 rounded">
                            Success
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-zinc-600 relative">2 min ago</td>
                      </tr>

                      {/* Ghost trace 2 */}
                      <tr className="border-b border-zinc-800 opacity-40 relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-zinc-700/10 to-transparent animate-shimmer"></div>
                        <td className="px-6 py-4 relative">
                          <div className="text-xs text-zinc-600 font-mono">z9y8x7w6v5u4t3s2...</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-zinc-600 relative">OpenAI</td>
                        <td className="px-6 py-4 text-sm text-zinc-600 relative">5</td>
                        <td className="px-6 py-4 text-sm font-mono text-zinc-600 relative">$0.0567</td>
                        <td className="px-6 py-4 text-sm text-zinc-600 relative">2.1s</td>
                        <td className="px-6 py-4 relative">
                          <span className="text-xs px-2 py-1 bg-green-950 text-green-700 border border-green-900 rounded">
                            Success
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-zinc-600 relative">5 min ago</td>
                      </tr>

                      {/* Ghost trace 3 */}
                      <tr className="border-b border-zinc-800 opacity-40 relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-zinc-700/10 to-transparent animate-shimmer"></div>
                        <td className="px-6 py-4 relative">
                          <div className="text-xs text-zinc-600 font-mono">m1n2o3p4q5r6s7t8...</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-zinc-600 relative">Google</td>
                        <td className="px-6 py-4 text-sm text-zinc-600 relative">2</td>
                        <td className="px-6 py-4 text-sm font-mono text-zinc-600 relative">$0.0123</td>
                        <td className="px-6 py-4 text-sm text-zinc-600 relative">0.9s</td>
                        <td className="px-6 py-4 relative">
                          <span className="text-xs px-2 py-1 bg-green-950 text-green-700 border border-green-900 rounded">
                            Success
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-zinc-600 relative">8 min ago</td>
                      </tr>

                      {/* Ghost trace 4 */}
                      <tr className="border-b border-zinc-800 opacity-40 relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-zinc-700/10 to-transparent animate-shimmer"></div>
                        <td className="px-6 py-4 relative">
                          <div className="text-xs text-zinc-600 font-mono">k9j8h7g6f5e4d3c2...</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-zinc-600 relative">Anthropic</td>
                        <td className="px-6 py-4 text-sm text-zinc-600 relative">4</td>
                        <td className="px-6 py-4 text-sm font-mono text-zinc-600 relative">$0.0445</td>
                        <td className="px-6 py-4 text-sm text-zinc-600 relative">1.8s</td>
                        <td className="px-6 py-4 relative">
                          <span className="text-xs px-2 py-1 bg-green-950 text-green-700 border border-green-900 rounded">
                            Success
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-zinc-600 relative">12 min ago</td>
                      </tr>
                    </>
                  ) : (
                    executions.map((exec) => (
                      <tr 
                        key={exec.trace_id}
                        onClick={() => navigate(`/executions/${exec.trace_id}`)}
                        className="border-b border-zinc-800 hover:bg-zinc-900 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="text-xs text-zinc-400 font-mono">{exec.trace_id?.substring(0, 16)}...</div>
                        </td>
                        <td className="px-6 py-4 text-sm">{exec.root_provider || 'Unknown'}</td>
                        <td className="px-6 py-4 text-sm">{exec.step_count || 1}</td>
                        <td className="px-6 py-4 text-sm font-mono">${(exec.total_cost || 0).toFixed(4)}</td>
                        <td className="px-6 py-4 text-sm">{((exec.total_duration_ms || 0) / 1000).toFixed(1)}s</td>
                        <td className="px-6 py-4">
                          <span className="text-xs px-2 py-1 bg-green-950 text-green-400 border border-green-900 rounded">
                            Success
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-zinc-400">
                          {exec.timestamp ? new Date(exec.timestamp).toLocaleString() : 'N/A'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
